import { useEffect, useState, useMemo } from "react";

import type { Options } from "highcharts";

import { LEGEND_POSITIONS, LineGraph } from "@similarweb/pro-logic-components";

import { translate } from "app/filters/i18nFilter";
import { percentageFilter } from "app/filters/ngFilters";
import { MAX_SELECTED_DOMAINS } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/influence-over-time-graph/consts";
import type { ISourceDomainResponse } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/types";
import { formatTopicToId } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/utils";
import { LLMAnnotationsContextProvider } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMAnnotationContextProvider";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { allTrackers } from "app/services/track/track";

import { assignDynamicLegendColors } from "../../../../shared/LLMTrackerUtils";

import { getGraphConfig } from "./config";
import type { SeriesLineData, SeriesLineItem } from "./types";
import { transformData } from "./utils";

interface IInfluenceOverTimeGraphProps {
    campaign: IAiTrackerCampaign;
    data: ISourceDomainResponse;
    topicId: string;
}

export const InfluenceOverTimeGraph = ({ campaign, data, topicId }: IInfluenceOverTimeGraphProps) => {
    const [transformedData, setTransformedData] = useState<SeriesLineData>();
    const topicFilteredData = useMemo<SeriesLineItem[]>(
        () => transformedData?.[formatTopicToId(topicId)] || [],
        [transformedData, topicId],
    );

    const graphConfig = useMemo<Options>(() => {
        const lineGraphData = topicFilteredData?.length ? topicFilteredData : [];
        return getGraphConfig(lineGraphData) as Options;
    }, [topicFilteredData]);

    const showMarkers = useMemo<boolean>(
        () => topicFilteredData?.some(({ visible, data: domainData }) => visible && domainData.length === 1),
        [topicFilteredData],
    );

    const onLegendSelection = (legendItems: SeriesLineItem[]): void => {
        const selectedIds = new Set(legendItems.map((item) => item.id));
        const prevIds = new Set(topicFilteredData.filter((b) => b.visible).map((b) => b.id));
        const enabledBrandId = [...selectedIds].find((id) => !prevIds.has(id));
        const disabledBrandId = [...prevIds].find((id) => !selectedIds.has(id));

        if (enabledBrandId) {
            const { name, total } = legendItems.find((item) => item.id === enabledBrandId);
            allTrackers.dispatchTrackEvent("Graph Filter", "Domain", `${name}/${(total * 100).toFixed(0)}%/add`);
        } else if (disabledBrandId) {
            const { name, total } = topicFilteredData.find((item) => item.id === disabledBrandId);
            allTrackers.dispatchTrackEvent("Graph Filter", "Domain", `${name}/${(total * 100).toFixed(0)}%/remove`);
        }

        //update the legend in all other topics if it exists there
        setTransformedData((prev) => {
            if (!prev) {
                return prev;
            }

            const currentTopicId = formatTopicToId(topicId);
            const changedDomains = new Map(
                topicFilteredData
                    .filter((item) => prevIds.has(item.id) !== selectedIds.has(item.id))
                    .map((item) => [item.name, selectedIds.has(item.id)]),
            );

            return Object.fromEntries(
                Object.entries(prev).map(([topicName, domains]) => {
                    const updatedDomains = domains.map((domain) => {
                        const isCurrentTopic = topicName === currentTopicId;
                        const shouldBeVisible = isCurrentTopic
                            ? selectedIds.has(domain.id)
                            : (changedDomains.get(domain.name) ?? domain.visible);

                        return isCurrentTopic || changedDomains.has(domain.name)
                            ? { ...domain, visible: shouldBeVisible }
                            : domain;
                    });

                    let selectedCount = 0;
                    const limitedDomains = updatedDomains.map((domain) => {
                        if (domain.visible && selectedCount < MAX_SELECTED_DOMAINS) {
                            selectedCount++;
                            return domain;
                        }

                        return domain.visible ? { ...domain, visible: false } : domain;
                    });

                    return [topicName, assignDynamicLegendColors(limitedDomains)];
                }),
            );
        });
    };

    useEffect(() => {
        setTransformedData(data && transformData(data));
    }, [data]);

    return (
        <LLMAnnotationsContextProvider
            key={`source-domains-influence-graph-${campaign?.id ?? ""}-${topicId}`}
            chartName="source-domains-influence-graph"
            campaign={campaign}
        >
            <LineGraph<SeriesLineItem>
                data={topicFilteredData}
                graphConfig={graphConfig}
                shouldPresentLegend
                shouldPresentValueAtLegend
                isSelectableCheckboxes
                isLegendSearch
                legendSearchPlaceholder={translate("llm.source.influence.graph.search.placeholder")}
                disabledLegendItemTooltip={translate("checkbox.max.selection")}
                withMarkers={showMarkers}
                legendsMaxWidth={285}
                legendsHeight="200px"
                legendHeaders={[
                    translate("llm.source.influence.graph.legend.header.domain"),
                    translate("llm.source.influence.graph.legend.header.influence"),
                ]}
                isToggleAllEnabled
                legendPosition={LEGEND_POSITIONS.RIGHT}
                legendValueFilter={(value) => percentageFilter()(value, 1)}
                legendToggleHandlerCallback={onLegendSelection}
                maxLegendItemsSelected={MAX_SELECTED_DOMAINS}
                keepSameLegendsOnDataUpdate={false}
            />
        </LLMAnnotationsContextProvider>
    );
};
